import type { ApiTaskStatus } from '@atlassian/conversation-assistant-service-api/types/assistance-service';

import type { ExecutionSegment, TaskProgressState } from '../chat/types';

// ─── API status → progress state mapping ──────────────────────────────────────

const API_STATUS_TO_PROGRESS_STATE: Record<ApiTaskStatus, TaskProgressState> = {
	DRAFT: 'Draft',
	QUEUED: 'Queued',
	RUNNING: 'Running',
	AWAITING_DISAMBIGUATION: 'AwaitingDisambiguation',
	AWAITING_PLAN_CONFIRMATION: 'AwaitingPlanConfirmation',
	AWAITING_ACTION_CONFIRMATION: 'AwaitingActionConfirmation',
	COMPLETED: 'Completed',
	FAILED: 'Failed',
	CANCELLED: 'Cancelled',
};

/**
 * Converts an API task status (SCREAMING_SNAKE_CASE) to the local domain
 * TaskProgressState (PascalCase). Shared by both legacy chat-store and the
 * unified tasks store so the mapping cannot drift.
 */
export const mapApiTaskStatusToProgressState = (status: ApiTaskStatus): TaskProgressState =>
	API_STATUS_TO_PROGRESS_STATE[status];

// ─── Execution segments merge ─────────────────────────────────────────────────

/**
 * Merges two sets of execution segments, deduplicating by overlap.
 *
 * Incoming segments take priority: for each existing segment, if its time
 * range overlaps with any incoming segment, the existing one is discarded.
 * Non-overlapping existing segments are retained. The result is sorted by
 * `startedAt` ascending.
 *
 * Shared by both legacy chat-store and the unified tasks store.
 */
export const mergeExecutionSegments = (
	existing: ExecutionSegment[] | null | undefined,
	incoming: ExecutionSegment[] | null | undefined,
): ExecutionSegment[] | null => {
	if (!incoming || incoming.length === 0) {
		return existing ?? null;
	}
	if (!existing || existing.length === 0) {
		return incoming;
	}

	const toMs = (iso: string) => new Date(iso).getTime();

	const incomingRanges = incoming.map((seg) => ({
		start: toMs(seg.startedAt),
		end: seg.endedAt ? toMs(seg.endedAt) : Date.now(),
	}));

	const overlaps = (seg: ExecutionSegment): boolean => {
		const start = toMs(seg.startedAt);
		const end = seg.endedAt ? toMs(seg.endedAt) : Date.now();
		return incomingRanges.some((r) => start < r.end && end > r.start);
	};

	const retained = existing.filter((seg) => !overlaps(seg));
	const merged = [...retained, ...incoming];

	return merged.sort((a, b) => toMs(a.startedAt) - toMs(b.startedAt));
};
